import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ensureMapsLoaded } from '../services/mapsLoader';

interface LocationInfoProps {
  address: string | null | undefined;
  variant?: 'default' | 'highlight';
}

interface DistanceResult { distance: string; duration: string; }

const highAccuracyOptions: PositionOptions = { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 };
const fallbackOptions: PositionOptions = { enableHighAccuracy: false, timeout: 15000, maximumAge: 0 };

export const LocationInfo: React.FC<LocationInfoProps> = ({ address, variant = 'default' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [origin, setOrigin] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [isAutocompleteReady, setIsAutocompleteReady] = useState(false);
  const [result, setResult] = useState<DistanceResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [autocompleteMessage, setAutocompleteMessage] = useState<string | null>(null);
  const originInputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<any>(null);

  const locationText = address || 'Location not specified';
  const isHighlight = variant === 'highlight';
  const hasOrigin = origin.trim().length > 0;
  const mapSource = hasOrigin && address
    ? `https://www.google.com/maps?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(address)}&output=embed`
    : `https://www.google.com/maps?q=${encodeURIComponent(locationText)}&output=embed`;
  const directionsUrl = hasOrigin && address
    ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(address)}&travelmode=driving`
    : null;

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || !originInputRef.current) return undefined;
    let cancelled = false;
    let placeListener: { remove?: () => void } | undefined;
    setIsAutocompleteLoading(true);
    setAutocompleteMessage(null);

    ensureMapsLoaded()
      .then((maps: any) => {
        if (cancelled || !originInputRef.current) return;
        if (!maps.places?.Autocomplete) throw new Error('places-unavailable');
        autocompleteRef.current = new maps.places.Autocomplete(originInputRef.current, {
          componentRestrictions: { country: 'my' },
          fields: ['formatted_address', 'geometry', 'name'],
          types: ['geocode'],
        });
        placeListener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();
          const selectedAddress = place?.formatted_address || place?.name;
          if (selectedAddress) {
            setOrigin(selectedAddress);
            setResult(null);
            setErrorMessage(null);
          }
        });
        setIsAutocompleteReady(true);
      })
      .catch(() => {
        if (!cancelled) setAutocompleteMessage('Google suggestions are unavailable. You can still type an address manually.');
      })
      .finally(() => { if (!cancelled) setIsAutocompleteLoading(false); });

    return () => {
      cancelled = true;
      placeListener?.remove?.();
      autocompleteRef.current = null;
      setIsAutocompleteReady(false);
    };
  }, [isModalOpen]);

  const updateOrigin = (value: string) => {
    setOrigin(value);
    setResult(null);
    setErrorMessage(null);
  };

  const openModal = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!address) return;
    setResult(null);
    setErrorMessage(null);
    setAutocompleteMessage(null);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const resolveCurrentLocation = (position: GeolocationPosition) => {
    const { latitude, longitude } = position.coords;
    updateOrigin(`${latitude},${longitude}`);
    setIsGettingLocation(false);
    ensureMapsLoaded().then((maps: any) => {
      new maps.Geocoder().geocode({ location: { lat: latitude, lng: longitude } }, (results: any[], status: string) => {
        if (status === 'OK' && results?.[0]?.formatted_address) updateOrigin(results[0].formatted_address);
      });
    }).catch(() => undefined);
  };

  const handleLocationError = (error: GeolocationPositionError) => {
    setIsGettingLocation(false);
    setErrorMessage(error.code === 1
      ? 'Location access was denied. Search for your starting address instead.'
      : error.code === 3
        ? 'Getting your location took too long. Search for your starting address instead.'
        : 'Your current location is unavailable. Search for your starting address instead.');
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setErrorMessage('Current location is not supported by this browser. Search for your address instead.');
      return;
    }
    setIsGettingLocation(true);
    setResult(null);
    setErrorMessage(null);
    navigator.geolocation.getCurrentPosition(
      resolveCurrentLocation,
      () => navigator.geolocation.getCurrentPosition(resolveCurrentLocation, handleLocationError, fallbackOptions),
      highAccuracyOptions,
    );
  };

  const calculateDistance = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!hasOrigin || !address) {
      setErrorMessage('Choose or enter a starting point before calculating.');
      return;
    }
    setIsCalculating(true);
    setResult(null);
    setErrorMessage(null);
    try {
      const maps: any = await ensureMapsLoaded();
      new maps.DistanceMatrixService().getDistanceMatrix({
        origins: [origin], destinations: [address], travelMode: maps.TravelMode.DRIVING, unitSystem: maps.UnitSystem.METRIC,
      }, (response: any, status: string) => {
        setIsCalculating(false);
        const route = response?.rows?.[0]?.elements?.[0];
        if (status === 'OK' && route?.status === 'OK') {
          setResult({ distance: route.distance.text, duration: route.duration.text });
        } else {
          setErrorMessage('Google Maps could not find a driving route between these locations.');
        }
      });
    } catch {
      setIsCalculating(false);
      setErrorMessage('Distance calculation is temporarily unavailable. Please try again later.');
    }
  };

  return (
    <>
      <div className={isHighlight
        ? 'inline-flex w-fit min-w-0 max-w-full items-center gap-2 rounded-xl border border-[#A1AAD9]/35 bg-[#FFF5D7] px-3 py-2 text-[#636363]'
        : 'flex min-w-0 flex-wrap items-center gap-2 text-[#636363]'}>
        {!isHighlight && <span className="flex-shrink-0">
          <svg className="h-5 w-5 text-[#A1AAD9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0Z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
        </span>}
        <span className={isHighlight ? 'flex min-w-0 flex-1 flex-col' : 'min-w-0 flex-1'}>
          {isHighlight && <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#5A46AA]">Location</span>}
          <span className={`block truncate text-sm ${isHighlight ? 'font-medium text-[#342F43]' : ''}`} title={locationText}>{locationText}</span>
        </span>
        {address && <button type="button" onClick={openModal} aria-label={`Check driving distance to ${locationText}`} title="Check driving distance" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-[#A1AAD9]/55 bg-white text-[#5A46AA] shadow-sm transition hover:border-[#5A46AA] focus:outline-none focus:ring-2 focus:ring-[#A1AAD9] focus:ring-offset-1"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg></button>}
      </div>

      {isModalOpen && address && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-[#342F43]/65 p-0 backdrop-blur-[2px] sm:items-center sm:p-4" onClick={event => event.stopPropagation()} onKeyDown={event => { event.stopPropagation(); if (event.key === 'Escape') closeModal(); }} role="dialog" aria-modal="true" aria-labelledby="distance-calculator-title">
          <style>{'.pac-container{z-index:10050!important;border-radius:12px;box-shadow:0 16px 40px rgba(52,47,67,.2);border:1px solid rgba(161,170,217,.5);overflow:hidden}.pac-item{padding:8px 12px;font-size:13px;cursor:pointer}.pac-item:hover{background:#FFFAED}.pac-item-query{font-size:14px;color:#342F43}'}</style>
          <section className="flex max-h-[96dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[28px] border border-[#A1AAD9]/40 bg-white shadow-[0_28px_90px_rgba(52,47,67,0.32)] sm:max-h-[90vh] sm:rounded-[26px]">
            <header className="flex flex-none items-start justify-between gap-4 border-b border-[#A1AAD9]/35 bg-gradient-to-r from-[#FFF5D7] to-[#FFFAED] px-5 py-4 sm:px-6 sm:py-5">
              <div className="flex min-w-0 items-start gap-3"><span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-white text-[#5A46AA] shadow-sm ring-1 ring-[#A1AAD9]/30"><svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M5 18h14M6.5 15.5l1.7-5.1A2 2 0 0 1 10.1 9h3.8a2 2 0 0 1 1.9 1.4l1.7 5.1M8 15.5v2m8-2v2" /></svg></span><div><span className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#5A46AA]">Plan your journey</span><h3 id="distance-calculator-title" className="mt-1 text-xl font-extrabold text-[#342F43] sm:text-2xl">Distance Calculator</h3><p className="mt-1 text-xs leading-5 text-[#636363] sm:text-sm">Choose your starting point to estimate the driving distance and travel time. Please note that the location shown is approximate and does not represent the actual location.</p></div></div>
              <button type="button" onClick={closeModal} aria-label="Close distance calculator" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl text-[#636363] hover:bg-white hover:text-[#5A46AA] focus:outline-none focus:ring-2 focus:ring-[#A1AAD9]"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="m6 6 12 12M18 6 6 18" /></svg></button>
            </header>
            <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[minmax(0,0.95fr)_minmax(320px,1.05fr)] lg:overflow-hidden">
              <form onSubmit={calculateDistance} className="flex flex-col gap-5 p-5 sm:p-6 lg:overflow-y-auto">
                <div><label htmlFor="distance-origin" className="block text-xs font-extrabold text-[#342F43]">Where are you starting from?</label><p className="mt-1 text-xs text-[#636363]">Search with Google suggestions or enter an address.</p><div className="relative mt-2.5"><svg className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#5A46AA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="m20 20-4-4" /></svg><input ref={originInputRef} id="distance-origin" type="text" value={origin} onChange={event => updateOrigin(event.target.value)} placeholder="Search starting location" autoComplete="off" className="min-h-12 w-full rounded-xl border border-[#A1AAD9]/60 bg-white py-3 pl-11 pr-10 text-sm text-[#342F43] shadow-sm outline-none focus:border-[#5A46AA] focus:ring-2 focus:ring-[#A1AAD9]/40" />{isAutocompleteLoading && <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-[#5A46AA]">Loading...</span>}</div>
                  <div className="mt-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><button type="button" onClick={getCurrentLocation} disabled={isGettingLocation} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#5A46AA] bg-white px-4 text-sm font-bold text-[#5A46AA] hover:bg-[#FFF5D7] focus:outline-none focus:ring-2 focus:ring-[#A1AAD9] disabled:opacity-60">{isGettingLocation ? 'Finding your location...' : 'Use current location'}</button>{isAutocompleteReady && <span className="text-center text-[11px] font-semibold text-green-700">Google suggestions ready</span>}</div>{autocompleteMessage && <p className="mt-2 text-xs leading-5 text-amber-700">{autocompleteMessage}</p>}
                </div>
                <div><span className="block text-xs font-extrabold text-[#342F43]">Job destination</span><div className="mt-2.5 flex items-start gap-3 rounded-xl border border-[#A1AAD9]/40 bg-[#FFFAED] px-3.5 py-3"><svg className="mt-0.5 h-5 w-5 flex-none text-[#5A46AA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg><span className="break-words text-sm font-bold leading-5 text-[#342F43]">{address}</span></div></div>
                <button type="submit" disabled={isCalculating || !hasOrigin} className="inline-flex min-h-12 w-full items-center justify-center rounded-xl border border-[#FF885C] bg-[#FF885C] px-5 text-sm font-extrabold text-white shadow-[0_8px_20px_rgba(255,136,92,0.24)] hover:bg-[#F57A4F] focus:outline-none focus:ring-2 focus:ring-[#FF885C] disabled:cursor-not-allowed disabled:border-[#D6D3DC] disabled:bg-[#D6D3DC] disabled:shadow-none">{isCalculating ? 'Calculating route...' : 'Calculate driving distance'}</button>
                <div aria-live="polite">{errorMessage && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{errorMessage}</div>}{result && <section className="rounded-2xl border border-[#A1AAD9]/45 bg-[#FFF5D7] p-4"><h4 className="font-extrabold text-[#342F43]">Trip summary</h4><div className="mt-3 grid grid-cols-2 gap-2.5"><div className="rounded-xl bg-white p-3 text-center"><span className="block text-[10px] font-bold uppercase text-[#636363]">Distance</span><strong className="text-xl text-[#5A46AA]">{result.distance}</strong></div><div className="rounded-xl bg-white p-3 text-center"><span className="block text-[10px] font-bold uppercase text-[#636363]">Drive time</span><strong className="text-xl text-[#5A46AA]">{result.duration}</strong></div></div>{directionsUrl && <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex min-h-10 w-full items-center justify-center rounded-xl border border-[#5A46AA] bg-white px-4 text-sm font-bold text-[#5A46AA]">Open route in Google Maps</a>}</section>}</div>
              </form>
              <div className="relative min-h-[260px] border-t border-[#A1AAD9]/35 bg-[#F7F8FC] lg:min-h-0 lg:border-l lg:border-t-0"><iframe key={mapSource} width="100%" height="100%" className="absolute inset-0 h-full min-h-[260px] w-full" style={{ border: 0 }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src={mapSource} title="Google map showing the job destination" /><span className="absolute left-3 top-3 rounded-lg border border-[#A1AAD9]/35 bg-white/95 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#5A46AA] shadow-sm">Google Map</span></div>
            </div>
          </section>
        </div>, document.body)}
    </>
  );
};
